import { useCallback, useState } from "react";
import {
  EventType,
  sendUserEvent,
} from "../../../helpers/GoogleAnalyticsHelper";
import { ApiErrorInterface } from "../../../interfaces/ApiErrorResponseInterface";

const PDF_TYPE = "application/pdf";
const PREVIEWABLE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"];
const PREVIEWABLE_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif"];

// Boron の MESSAGE_MAX_FILE_SIZE_MB（既定 10）と一致させる。ちょうど 10MB は通す
export const MAX_PDF_BYTES = 10 * 1024 * 1024;
// NOTE: 送信時に Boron が返す 422 の文言に合わせる（項目名「PDF」+ max_size_error。4 つの送信フォームとも同じ）
export const PDF_SIZE_ERROR_MESSAGE =
  "PDFファイルサイズは10MB以下である必要があります";

// NOTE: OS に拡張子と種類の関連付けがないと、選んだファイルの type が空になる。
//       空のときは拡張子で判定し、プレビューを経ずに添付される経路を作らない
const hasExtension = (file: File, extensions: string[]) =>
  file.type === "" &&
  extensions.some((ext) => file.name.toLowerCase().endsWith(ext));

export const isPdf = (file: File) =>
  file.type === PDF_TYPE || hasExtension(file, [".pdf"]);

const isPreviewable = (file: File) =>
  isPdf(file) ||
  PREVIEWABLE_IMAGE_TYPES.includes(file.type) ||
  hasExtension(file, PREVIEWABLE_IMAGE_EXTENSIONS);

// キャンセル回数（ヒヤリハット）を数える。
// NOTE: 当面は GA のイベントで数える（2026-10-29 決定）。社内の記録に変えるかは仕様書の未決事項
const recordPreviewCancel = (file: File) =>
  sendUserEvent({
    type: EventType.CancelAttachmentPreview,
    fileType: isPdf(file) ? "pdf" : "image",
  });

type Props = {
  changeFormFile: (file: File) => void;
  // 宛先を表すキー。変わったらプレビューとサイズ超過のエラーを捨てる
  destinationKey: string;
};

/**
 * 添付するファイルを、送る前にプレビュー画面で確かめさせる
 * - 画像（jpeg・png・gif）と PDF はプレビューを通し、「添付する」で初めて添付する
 * - 上限を超える PDF はプレビューを開かず、エラーを返す
 * - それ以外の形式は今までどおりそのまま添付し、送信時のエラーに任せる
 */
export const useAttachmentPreview = ({
  changeFormFile,
  destinationKey,
}: Props) => {
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [prevDestinationKey, setPrevDestinationKey] = useState(destinationKey);

  // 選んだときの宛先にプレビューを結びつけ、別の宛先に添付されないようにする。
  // 描画中に捨てるので、古い宛先のプレビューが一瞬でも出ることはない。
  // 宛先を戻しても、捨てたものは戻らない
  if (prevDestinationKey !== destinationKey) {
    setPrevDestinationKey(destinationKey);
    setPreviewFile(null);
    setFileError(null);
  }

  const fileErrors: ApiErrorInterface[] = fileError
    ? [{ field: "file", message: fileError }]
    : [];

  const selectFile = (file: File) => {
    setFileError(null);

    if (isPdf(file) && file.size > MAX_PDF_BYTES) {
      setFileError(PDF_SIZE_ERROR_MESSAGE);
      return;
    }

    if (isPreviewable(file)) {
      setPreviewFile(file);
      return;
    }

    changeFormFile(file);
  };

  const confirm = () => {
    if (!previewFile) {
      return;
    }
    changeFormFile(previewFile);
    setPreviewFile(null);
  };

  const cancel = () => {
    if (previewFile) {
      recordPreviewCancel(previewFile);
    }
    setPreviewFile(null);
  };

  const clearFileError = useCallback(() => setFileError(null), []);

  return {
    previewFile,
    fileErrors,
    selectFile,
    confirm,
    cancel,
    clearFileError,
  };
};
